<template>
  <div class="property-price-range mt-3 border-top pt-3">
    <!-- 💰 Цена -->
    <div class="text-dark fw-semibold">
      💰 {{ $t('price_estimation.price_estimation.price_label') }} € {{ formatNumber(price) }}
    </div>

    <!-- 🧩 Проверяем наличие прогнозов -->
    <template v-if="hasPredictions">
      <div class="text-secondary small">
        {{ $t('price_estimation.price_estimation.market_range') }}: € {{ formatNumber(medianPrice) }} – €
        {{ formatNumber(averagePrice) }}
      </div>

      <!-- 🌈 Графический диапазон -->
      <div class="market-range mt-2 position-relative">
        <div class="range-bar"></div>
        <div class="price-marker" :style="{ left: pricePosition + '%' }">
          <div class="marker-dot"></div>
        </div>
      </div>

      <!-- ⚖️ Вердикт -->
      <div
        class="mt-2 fw-semibold"
        :class="{
          'text-success': status.inRange,
          'text-danger': status.isAbove,
          'text-primary': status.isBelow
        }"
      >
        <template v-if="status.inRange">
          {{ $t('price_estimation.price_estimation.within_range') }}
        </template>
        <template v-else-if="status.isAbove">
          {{ $t('price_estimation.price_estimation.above_range', { percent: status.diffPercent }) }}
        </template>
        <template v-else>
          {{ $t('price_estimation.price_estimation.below_range', { percent: status.diffPercent }) }}
        </template>
      </div>
    </template>

    <!-- 🚫 Если данных нет -->
    <template v-else>
      <div class="text-muted small mt-2">
        {{ $t('price_estimation.price_estimation.not_ready') }}
      </div>
    </template>
  </div>
</template>

<script setup>
import { computed } from 'vue'

const props = defineProps({
  price: { type: Number, required: true },
  medianPrice: { type: Number, default: null },
  averagePrice: { type: Number, default: null }
})

// 🧠 Проверяем наличие прогнозов
const hasPredictions = computed(() =>
  props.medianPrice && props.averagePrice && props.medianPrice > 0 && props.averagePrice > 0
)

// 📊 Статус цены
const status = computed(() => {
  const price = props.price
  const median = props.medianPrice
  const avg = props.averagePrice
  if (!price || !median || !avg) return {}

  const low = Math.min(median, avg)
  const high = Math.max(median, avg)
  const avgRange = (low + high) / 2
  const diffPercent = ((price - avgRange) / avgRange) * 100

  return {
    inRange: price >= low && price <= high,
    isAbove: price > high,
    isBelow: price < low,
    diffPercent: Math.abs(diffPercent.toFixed(1)),
    low,
    high
  }
})

// 📏 Позиция маркера
const pricePosition = computed(() => {
  if (!status.value.low || !status.value.high) return 50
  const extendedLow = status.value.low * 0.85
  const extendedHigh = status.value.high * 1.15
  const pos = ((props.price - extendedLow) / (extendedHigh - extendedLow)) * 100
  return Math.min(100, Math.max(0, pos))
})

// 💶 Форматирование числа
const formatNumber = (num) => {
  if (!num || isNaN(num)) return '—'
  return Number(num).toLocaleString('ru-RU', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  })
}
</script>

<style scoped>
.market-range {
  height: 8px;
  border-radius: 6px;
  background: linear-gradient(
    90deg,
    #198754 0%,   /* зелёный (дешево) */
    #ffc107 50%,  /* жёлтый (средне) */
    #dc3545 100%  /* красный (дорого) */
  );
  position: relative;
  overflow: hidden;
}

.price-marker {
  position: absolute;
  top: -3px;
  transform: translateX(-50%);
  transition: left 0.4s ease;
}

.marker-dot {
  width: 12px;
  height: 12px;
  border-radius: 50%;
  border: 2px solid #fff;
  background-color: #000;
  box-shadow: 0 0 4px rgba(0, 0, 0, 0.4);
}
</style>
